import clsx from 'clsx'
import React, { useLayoutEffect, useMemo, useState } from 'react'

import { TableActionColumnHeader } from './ActionColumnHeader'
import { TableActionsCell } from './ActionsCell'
import { TableBody } from './Body'
import { TableCell } from './Cell'
import { TableColumnHeader } from './ColumnHeader'
import { TableContext } from './context'
import { TableHead } from './Head'
import { TableRow } from './Row'
import { TableRowHeader } from './RowHeader'
import { TableSelectionCell } from './SelectionCell'
import { TableSelectionColumnHeader } from './SelectionColumnHeader'

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
  width?: 'full' | 'auto';
}

/**
 * A hook that calculates and sets the left offset of all sticky cells in the
 * table. It observes sticky cells in the first row to detect layout changes
 * to only recalculate the offsets when necessary.
 */
function useStickyOffsets ({
  tableRef,
  hasXOverflow,
}: {
  tableRef: React.RefObject<HTMLTableElement>;
  hasXOverflow: boolean;
}) {
  useLayoutEffect(() => {
    // There's no point measuring and assigning offsets to sticky cells if the
    // table is not horizontally scrollable.
    if (!tableRef.current || !hasXOverflow) return

    // We'll observe only the first row for any sticky cells that are resized,
    // since in a table layout the width of all the cells in a column will be
    // the same.
    let observedRow = tableRef.current.querySelector('tr')

    function setStickyOffsets () {
      const stickyColumnOffsets: Record<number, number> = {}

      tableRef.current?.querySelectorAll('tr').forEach((row, rowIndex) => {
        let offset = 0

        // We take measurements from the first row to determine what the left
        // offset of each sticky column should be. We only need to measure cells
        // in the first row because in a table layout the width of all the cells
        // in that column will be the same.

        // We then apply the measured offsets from the first row to sticky cells
        // in the rest of the rows.
        row
          .querySelectorAll('[data-sticky="true"]')
          .forEach((element, columnIndex) => {
            const cell = element as HTMLElement

            if (rowIndex === 0) {
              stickyColumnOffsets[columnIndex] = offset
              cell.style.setProperty('left', `${offset}px`)
              offset += cell.offsetWidth
            } else {
              cell.style.setProperty(
                'left',
                `${stickyColumnOffsets[columnIndex]}px`
              )
            }
          })
      })
    }

    function observeStickyCells () {
      const stickyCells = observedRow?.querySelectorAll('[data-sticky="true"]')

      stickyCells?.forEach((cell) => {
        resizeObserver.observe(cell)
      })
    }

    // When any observed cell is resized, we need recalculate all of the offsets.
    const resizeObserver = new ResizeObserver(() => {
      setStickyOffsets()
    })

    // If the first row is removed, we need to find the new first row and
    // observe the sticky cells in it. And if a new row is added, we need to
    // set the sticky offsets for its cells.
    const mutationObserver = new MutationObserver((entries) => {
      const newFirstRow = tableRef.current?.querySelector('tr') || null
      const rowsWereAdded = entries.some((entry) => {
        return Array.from(entry.addedNodes).some((node) => {
          return node instanceof HTMLElement && node.tagName === 'TR'
        })
      })

      if (newFirstRow !== observedRow) {
        observedRow = newFirstRow
        observeStickyCells()
      } else if (rowsWereAdded) {
        setStickyOffsets()
      }
    })

    observeStickyCells()
    mutationObserver.observe(tableRef.current, {
      childList: true,
      subtree: true,
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [hasXOverflow])
}

/**
 * A hook that determines whether the table has horizontal overflow.
 */
function useXOverflow ({ tableRef, wrapperRef }: { tableRef: React.RefObject<HTMLTableElement>, wrapperRef: React.RefObject<HTMLDivElement> }) {
  const [hasXOverflow, setHasXOverflow] = useState(false)

  useLayoutEffect(() => {
    const tableElement = tableRef.current
    const observer = new IntersectionObserver(
      (entries) => {
        setHasXOverflow(entries[0].intersectionRatio < 1)
      },
      {
        root: wrapperRef.current,
        threshold: [0.99, 1],
      }
    )

    if (tableElement) {
      observer.observe(tableElement)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  return { hasXOverflow }
}

export function Table ({ children, width = 'full', ...props }: TableProps) {
  const [isScrolled, setScrolled] = useState(false)

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<HTMLTableElement>(null)

  const { hasXOverflow } = useXOverflow({ tableRef, wrapperRef })

  useStickyOffsets({ tableRef, hasXOverflow })

  const contextValue = useMemo(() => ({ isScrolled }), [isScrolled])

  return (
    <div
      className={clsx('overflow-x-auto relative', {
        'outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500':
          hasXOverflow,
      })}
      data-testid='table-scroll-wrapper'
      tabIndex={hasXOverflow ? 0 : undefined}
      onScroll={(event) => {
        setScrolled((event.target as HTMLDivElement).scrollLeft > 0)
      }}
      ref={wrapperRef}
    >
      <TableContext.Provider value={contextValue}>
        <table
          {...props}
          className={clsx('border-separate border-spacing-0', {
            'w-full': width === 'full',
          })}
          ref={tableRef}
          style={{ '--cell-spacing': '1.5rem' } as React.CSSProperties}
        >
          {children}
        </table>
      </TableContext.Provider>
    </div>
  )
}

Table.ActionsCell = TableActionsCell
Table.ActionColumnHeader = TableActionColumnHeader
Table.Body = TableBody
Table.Cell = TableCell
Table.SelectionCell = TableSelectionCell
Table.SelectionColumnHeader = TableSelectionColumnHeader
Table.ColumnHeader = TableColumnHeader
Table.Head = TableHead
Table.Row = TableRow
Table.RowHeader = TableRowHeader
